import { Edit16 } from '@carbon/icons-react';
import { Col, Row, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';
import { User } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { Menu } from 'components/atoms/dropdown';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Tag } from 'components/atoms/tag';
import { fullName } from 'utils/full-name';
import { isChildNode } from 'utils/is-child-node';
import { usePeopleDrawerParamsFromUrl } from '../../people-drawer/hooks/use-people-drawer-params';
import { useManagerOfUsers } from '../../people-list/hooks/use-manager-of';
import { useContactType } from '../hooks/use-contact-type';
import { OrgNode as OrgNodeType } from '../types';
import { EditOrgNode } from './EditOrgNode';
import { PeopleEditor } from './PeopleEditor';

interface Props {
  user: User;
  selected?: boolean;
  className?: string;
  childNodes: OrgNodeType[];
  parent: User | null;
  siblingCount: number;
  onClick?: () => void;
}

const MoreActionsCol = styled(Col)<{ isMoreOptionsVisible: boolean }>`
  /* position: absolute; */
  top: 0%;
  right: 8px;
  visibility: ${(props) => (props.isMoreOptionsVisible ? 'visible' : 'hidden')};
`;

const StyledCard = styled(animated.div)<{ selected: boolean }>`
  background: white;
  display: flex;
  position: relative;
  z-index: 1;
  /* justify-content: center; */
  padding: 0 var(--spacing-04);
  width: ${(props) => (props.selected ? '320px' : '272px')};
  height: ${(props) => (props.selected ? '96px' : '80px')};
  transition: box-shadow 0.3s ease-in-out;
  box-shadow: ${(props) =>
    props.selected ? 'var(--shadow-08)' : 'var(--shadow-02)'};

  &:hover {
    box-shadow: var(--shadow-08);
  }

  .people-user-name {
    font-size: ${(props) => (props.selected ? '16px' : '14px')};

    &:hover {
      text-decoration: underline;
    }
  }

  .people-user-role {
    font-size: ${(props) => (props.selected ? '14px' : '12px')};
    color: var(--gray-70);
    flex-grow: 1;
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: ${(props) => (props.selected ? '70%' : '80%')};
  }

  .hide-large-text {
    display: block;
    text-overflow: ellipsis;
    max-width: 12ch;
    overflow: hidden;
    white-space: nowrap;
  }

  .ellipsis-text-10ch {
    max-width: 10ch;
  }

  &:hover {
    ${MoreActionsCol} {
      visibility: visible;
    }
  }
`;

const StyledMenu = styled(Menu)`
  box-shadow: var(--shadow-08);
  height: max-content;
`;

const StyledDiv = styled.div``;

const PeopleEditMenuDiv = styled.div`
  display: flex;
  position: absolute;
  /* flex-direction: column; */
  /* align-items: flex-end; */
`;

export const PeopleEditMenu = (props) => {
  const { user, hideDropdown } = props;

  const { data: users, isLoading } = useManagerOfUsers(user.userId);

  const [isReplaceMenuVisible, setIsReplaceMenuVisible] = useState(false);

  /* Empty selectedKeys as the state was maintained if react select caused the component to close */
  return (
    <PeopleEditMenuDiv onBlur={() => hideDropdown()}>
      <StyledMenu
        className="ant-dropdown-menu ant-dropdown-menu-light ant-dropdown-menu-root ant-dropdown-menu-vertical"
        selectedKeys={[]}
        onClick={(e) => {
          if (e.key === 'replace-user') {
            return;
          }
          hideDropdown();
        }}
      >
        <Menu.Item
          onClick={() => setIsReplaceMenuVisible(true)}
          className="ant-dropdown-menu-item"
          key="replace-user"
        >
          <FormattedMessage id="customer_company.org_chart.replace_user" />
        </Menu.Item>
      </StyledMenu>
      <OutsideClickHandler
        allowed={isChildNode(['submenu-option-org'])}
        onOutsideClick={() => setIsReplaceMenuVisible(false)}
      >
        <StyledDiv className="submenu-option-org">
          {isReplaceMenuVisible && (
            <PeopleEditor
              isMulti={false}
              title="Replace User"
              users={users}
              selected={[]}
              isLoading={isLoading}
              onComplete={() => {
                setIsReplaceMenuVisible(false);
                hideDropdown();
              }}
            />
          )}
        </StyledDiv>
      </OutsideClickHandler>
    </PeopleEditMenuDiv>
  );
};

const StyledButton = styled.div<{ selected: boolean; childCount: number }>`
  --parent-width: ${(props) => (props.selected ? '324px' : '256px')};
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: -1em;
  border: 1px solid var(--coolest-gray-30);
  border-radius: 25px;
  /* padding: 0 var(--spacing-02); */
  width: 32px;
  left: 50%;
  height: 24px;
  font-size: 12px;
  left: calc(0.5 * var(--parent-width));
  z-index: 2;
  background: white;
  transform: translate(-50%);
  visibility: ${(props) => (props.childCount > 0 ? 'visible' : 'hidden')};
  transition: all var(--animation-duration-fast) ease-in; /* Edit button grow and color effects */

  ${StyledCard}:hover &, &.visible-button {
    visibility: visible;
    transition: all 0.25px;
    transition-delay: 1ms;
    width: 54px;
    flex-basis: 2;
  }

  &:hover,
  &.visible-button {
    background: var(--blue-10);
  }
`;

const AddMemberCol = styled.div`
  display: none;
  width: 100%;
  height: 100%;
  transition: all 250ms;

  ${StyledCard}:hover &, &.visible-button {
    display: flex;
    justify-content: center;
    flex: 50%;
    align-items: center;
  }
`;

const ButtonRow = styled(Row)`
  width: 100%;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`;

const MemberCountCol = styled(Col)`
  flex: 50%;
  display: flex;
  justify-content: center;

  ${StyledCard}:hover & {
    display: none;
  }
`;

const ChampionCol = styled(Col)`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`;

const StyledContainer = styled.div`
  /* position: absolute;
  left: 100%;
  bottom: -200%; */
`;

const StyledName = styled.div``;

const SiblingPresenceIndicatorStyle = styled.div`
  background: white;
  display: flex;
  position: absolute;
  padding: 0 var(--spacing-04);
  width: 320px;
  height: 96px;
  box-shadow: var(--shadow-08);
`;

export const getUserFullName = (user: Pick<User, 'firstName' | 'lastName'>) =>
  user.lastName ? `${user.firstName} ${user.lastName}` : `${user.firstName}`;

export const OrgNode = (props: Props) => {
  const {
    user,
    selected,
    className,
    childNodes,
    parent,
    siblingCount,
    onClick: onClickHandler,
  } = props;
  const mainCardSpring = useSpring({
    from: { transform: 'scale(0.5)' },
    to: { transform: 'scale(1)' },
    // config: {
    //   tension: 400,
    //   velocity: -0.01,
    //   clamp: true,
    //   friction: 25,
    //   precision: 0.01,
    //   mass: 1,
    // },
    config: {
      duration: 200,
    },
  });
  const [addMenuVisible, setAddMenuVisible] = useState(false);

  const hoverCardRef = useRef<HTMLDivElement | null>(null);
  const btnPillRef = useRef<HTMLDivElement | null>(null);
  const addMemberBtnRef = useRef<HTMLDivElement | null>(null);

  const { show } = usePeopleDrawerParamsFromUrl();

  const userContact = useContactType(user);

  const userFullName = fullName(user);

  useEffect(() => {
    const refArr = [hoverCardRef, btnPillRef, addMemberBtnRef];

    if (addMenuVisible) {
      refArr.map((node) => node.current?.classList.add('visible-button'));
    } else {
      refArr.map((node) => node.current?.classList.remove('visible-button'));
    }
  }, [addMenuVisible]);

  return (
    <div>
      <animated.div
        style={{ position: 'relative', ...(selected ? mainCardSpring : {}) }}
      >
        <StyledCard
          selected={selected ?? false}
          onClick={() => onClickHandler?.()}
          className={
            className ? className.concat(' org-node-card') : 'org-node-card'
          }
          ref={hoverCardRef}
          data-cy={`org_chart.node.${fullName(user).replaceAll(' ', '_')}`}
        >
          <Row
            gutter={selected ? 16 : 8}
            align="middle"
            wrap={false}
            style={{ width: '100%', marginLeft: 0, marginRight: 0 }}
          >
            <Col>
              <Avatar size={selected ? 64 : 40} name={fullName(user)} />
            </Col>
            <Col style={{ flexBasis: '40%' }}>
              <Row wrap={false}>
                <StyledName
                  className="people-user-name hide-large-text"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    show(user.userId);
                  }}
                >
                  <Tooltip title={userFullName}>{userFullName}</Tooltip>
                </StyledName>
              </Row>
              <Row
                className={`people-user-role ${
                  userContact !== null && 'hide-large-text'
                }`}
                wrap={false}
              >
                {(user.role?.roleName && user.role?.roleName) || ''}
              </Row>
            </Col>
            {userContact !== null ? (
              <ChampionCol>
                <Tooltip title={userContact.metaFieldValue?.label}>
                  <Tag
                    color={userContact.metaFieldValue?.color}
                    className="hide-large-text"
                  >
                    {userContact.metaFieldValue?.label}
                  </Tag>
                </Tooltip>
              </ChampionCol>
            ) : null}
          </Row>

          <StyledButton
            ref={btnPillRef}
            selected={selected ?? false}
            childCount={childNodes.length}
            onClick={(event) => event.stopPropagation()}
          >
            <ButtonRow wrap={false}>
              {childNodes.length > 0 && !addMenuVisible && (
                <MemberCountCol>{childNodes.length}</MemberCountCol>
              )}
              <AddMemberCol
                ref={addMemberBtnRef}
                onClick={() => setAddMenuVisible(true)}
                role="button"
                data-cy="org_chart.node.edit"
              >
                <Edit16 />
              </AddMemberCol>
            </ButtonRow>
          </StyledButton>
        </StyledCard>
        {selected && siblingCount > 1 && (
          <SiblingPresenceIndicatorStyle style={{ top: '-8%', left: '2%' }} />
        )}
      </animated.div>
      {addMenuVisible ? (
        <OutsideClickHandler
          allowed={isChildNode(['ant-popover-inner-content'])}
          onOutsideClick={() => setAddMenuVisible(false)}
        >
          <StyledContainer>
            <EditOrgNode
              user={user}
              parent={parent}
              selected={childNodes}
              onComplete={() => setAddMenuVisible(false)}
            />
          </StyledContainer>
        </OutsideClickHandler>
      ) : null}
    </div>
  );
};
